import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { type FC, type FormEvent, type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { useWeekIndex } from "../contexts/WeekIndexProvider";
import { patchScheduleDay } from "../hooks/scheduleCacheWrites";
import { useRosterLookup } from "../hooks/useRosterLookup";
import { useScheduleCacheEnabled } from "../hooks/useScheduleWeekRules";
import { useFetchScheduleWeeks } from "../hooks/useScheduleWeeks";
import { useShownWeek } from "../hooks/useShownWeek";
import {
	useChatbotAllConversations,
	useChatbotConversation,
	useChatbotConversations,
	useDeleteConversation,
	useSendChatBotMessage,
	useSendUserGuideMessage,
} from "../hooks/useUserGuide";
import { type Chat, ChatbotActions, type ChatbotMessage, type ChatMessage, type WeekData } from "../utils/models";
import { weekIndexOfDate } from "../utils/panelWeeks";
import { useAuth } from "./AuthProvider";
import { buildAnnieShiftInsightsAnswer, detectAnnieShiftInsightsIntent } from "./annieShiftInsightsAnswers";
import { planDemoReplacement } from "./DemoReplaceNurse";
import { useHospitalData } from "./HospitalDataProvider";
import { useSnackbar } from "./SnackbarContext";
import { UserGuideChatContext } from "./UserGuideChatContext";

const GREETING_MESSAGES = [
	"Hey, Annie here! Feel free to ask me any question about how to use the In-House app",
	"Hi there! I'm Annie, your the In-House app guide. What can I help you with today?",
	"Welcome! I'm Annie and I'm here to help you navigate the In-House app. Ask me anything!",
	"Hello! Annie at your service. Need help with the In-House app? Just ask!",
	"Hey! I'm Annie, ready to answer any questions you have about using the In-House app",
];

const DEMO_GREETING_MESSAGES = ["Hi, I'm Annie your AI scheduler assistant, here at your service! How can I help you today?"];

function getNurseDemoPickupMessage(nurseName: string, success: boolean): ChatMessage {
	return {
		id: Date.now() + 1,
		text: success ? `✅ **Success!** ${nurseName} can pick up the shift 🎉` : `${nurseName} could not pick up the shift`,
		sender: "server",
		timestamp: new Date().toISOString(),
		type: "text",
	};
}

export const UserGuideChatProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const [chats, setChats] = useState<Chat[]>([]);
	const [allConversations, setAllConversations] = useState<Chat[]>([]);
	const [activeChatId, setActiveChatId] = useState<string>("");
	const [isOpen, setIsOpen] = useState(false);
	const [isHiding, setIsHiding] = useState(false);
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const { userInfo } = useAuth();
	const {
		showErrorSnackbar,
		chosenDepartment,
		isAnyDemo,
		isRealDemo,
		isHospitalView,
		isHospitalDemo,
		hospitalData,
		addNurseToShift,
		removeNurseFromSpecificDate,
		prepareDataForAddNurse,
	} = useHospitalData();
	const isCacheEnabled = useScheduleCacheEnabled();
	const fetchScheduleWeeks = useFetchScheduleWeeks();
	const queryClient = useQueryClient();
	const { roster } = useRosterLookup(chosenDepartment.department_id);

	// The demo's cover: with the cache on it edits the cached day the grid draws; off, the old list, as it always did.
	// Nothing is saved: demo departments don't save writes. Resolves whether someone was put on the shift.
	const replaceDemoNurse = useCallback(
		async (nurseId: number, date: DateTime, replacementName: string): Promise<boolean> => {
			const departmentId = chosenDepartment.department_id;
			if (!isCacheEnabled) {
				const plan = planDemoReplacement(hospitalData, nurseId, date, replacementName, departmentId);
				if (!plan) return false;
				// Legacy demos script a name no one has; the covering shift shows it, as it always did.
				const [firstName, ...lastNames] = replacementName.split(" ");
				plan.replacement.firstName = firstName || replacementName;
				plan.replacement.lastName = lastNames.join(" ");
				const nurseToAdd = prepareDataForAddNurse(
					hospitalData,
					plan.date,
					plan.shiftType,
					plan.replacement.id,
					plan.shift.location,
					false,
					undefined,
					undefined,
					undefined,
					0,
				);
				if (!nurseToAdd) return false;
				removeNurseFromSpecificDate(plan.date, plan.shiftType, nurseId, false, plan.shift.id, true);
				addNurseToShift(nurseToAdd, plan.date, plan.shiftType, false, true);
				return true;
			}
			// DEFAULT: no one until the roster arrives; the demo then covers nobody, as it would with no eligible colleague.
			const plan = planDemoReplacement({ weeks: await fetchScheduleWeeks(date), nurses: roster ?? [] }, nurseId, date, replacementName, departmentId);
			if (!plan) return false;
			await patchScheduleDay(queryClient, departmentId, plan.date, (day) => ({
				...day,
				scheduled: day.scheduled.map((shift) => (shift.id === plan.shift.id ? { ...shift, nurseId: plan.replacement.id } : shift)),
			}));
			return true;
		},
		[
			isCacheEnabled,
			hospitalData,
			chosenDepartment.department_id,
			prepareDataForAddNurse,
			removeNurseFromSpecificDate,
			addNurseToShift,
			fetchScheduleWeeks,
			roster,
			queryClient,
		],
	);
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const messageEndRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const { data: currentChats, error: currentChatsError } = useChatbotConversations();
	const { data: allConversationsData, isLoading: isLoadingAllConversations } = useChatbotAllConversations();
	const { mutateAsync: sendChatBotMessage } = useSendChatBotMessage();
	const { mutateAsync: sendUserGuideMessage } = useSendUserGuideMessage();
	const { data: currentChatMessages } = useChatbotConversation(activeChatId);
	const { mutateAsync: deleteConversation, isPending: isDeletingConversation } = useDeleteConversation();
	const { weekIndex } = useWeekIndex();
	const { weekStart: shownWeekStart } = useShownWeek();

	// Hospital demo answers workload questions locally (see annieShiftInsightsAnswers).
	const isShiftInsightsDemo = isHospitalView || isHospitalDemo;
	// The week those answers read: the shown one, from the cache when it's on. Rejects when that week fails to load.
	const readShownWeek = useCallback(async (): Promise<WeekData | undefined> => {
		if (!isCacheEnabled) return hospitalData?.weeks?.[weekIndex];
		// DEFAULT: today's week until a page names one, as the staffing pages open.
		const weekStart = shownWeekStart ?? DateTime.now();
		const weeks = await fetchScheduleWeeks(weekStart);
		return weeks[weekIndexOfDate(weeks, weekStart)];
	}, [isCacheEnabled, hospitalData?.weeks, weekIndex, shownWeekStart, fetchScheduleWeeks]);

	// Demo depts (legacy fake-data OR real inhouse2__ depts) route to the action chatbot
	// (RemoveNurse, etc.); everyone else gets the read-only user-guide help assistant.
	const usesActionChat = isAnyDemo || isRealDemo;
	const sendMessage = usesActionChat ? sendChatBotMessage : sendUserGuideMessage;

	const activeChat = useMemo<Chat | undefined>(() => {
		if (!activeChatId) return undefined;

		const chatInChats = chats.find((chat) => chat.id === activeChatId);
		if (chatInChats) return chatInChats;

		if (userInfo?.support) return allConversations.find((chat) => chat.id === activeChatId);

		return undefined;
	}, [activeChatId, chats, allConversations, userInfo?.support]);

	const createNewChat = useCallback((): Chat => {
		const greetingSource = usesActionChat ? DEMO_GREETING_MESSAGES : GREETING_MESSAGES;
		const greetingMessage = greetingSource[Math.floor(Math.random() * greetingSource.length)];

		const greetingChatMessage: ChatMessage = {
			id: Date.now(),
			text: greetingMessage,
			sender: "server",
			timestamp: new Date().toISOString(),
			type: "text",
		};
		return {
			id: v4(),
			messages: [greetingChatMessage],
			inputMessage: "",
			isThinking: false,
			isTyping: false,
			streamingMessageId: greetingChatMessage.id,
			unreadCount: 0,
		};
	}, [usesActionChat]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: showErrorSnackbar re-renders too often
	useEffect(() => {
		if (currentChatsError) {
			showErrorSnackbar("Failed to load conversations", {}, currentChatsError);
			const initialChat = createNewChat();
			setChats([initialChat]);
			setActiveChatId(initialChat.id);
			return;
		}

		const chatMap = (currentChats ?? []).reduce((map, msg) => {
			const arr = map.get(msg.sessionId) ?? [];
			arr.push({
				id: `${msg.sessionId}-${msg.timestamp}`,
				text: msg.message,
				sender: msg.role === "user" ? "user" : "server",
				timestamp: msg.timestamp,
				type: "text",
			});
			map.set(msg.sessionId, arr);
			return map;
		}, new Map<string, ChatMessage[]>());

		const loadedChats: Chat[] = Array.from(chatMap.entries()).map(([sessionId, messages]) => ({
			id: sessionId,
			messages: messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
			inputMessage: "",
			isThinking: false,
			isTyping: false,
			streamingMessageId: null,
			unreadCount: 0,
		}));

		if (loadedChats.length) {
			setChats(loadedChats);
			setActiveChatId(loadedChats[0].id);
		} else {
			const initialChat = createNewChat();
			setChats([initialChat]);
			setActiveChatId(initialChat.id);
		}
	}, [currentChats, createNewChat]);

	useEffect(() => {
		if (!allConversationsData) return;

		const chatMap = allConversationsData.reduce((map, msg) => {
			const arr = map.get(msg.sessionId) ?? [];
			arr.push({
				id: `${msg.sessionId}-${msg.timestamp}`,
				text: msg.message,
				sender: msg.role === "user" ? "user" : "server",
				timestamp: msg.timestamp,
				type: "text",
			});
			map.set(msg.sessionId, arr);
			return map;
		}, new Map<string, ChatMessage[]>());

		const parsedConversations: Chat[] = Array.from(chatMap.entries()).map(([sessionId, messages]) => ({
			id: sessionId,
			messages: messages.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()),
			inputMessage: "",
			isThinking: false,
			isTyping: false,
			streamingMessageId: null,
			unreadCount: 0,
		}));

		setAllConversations(parsedConversations);
	}, [allConversationsData]);

	const updateChat = useCallback(
		(chatId: string, updates: Partial<Chat>) => setChats((prev) => prev.map((chat) => (chat.id === chatId ? { ...chat, ...updates } : chat))),
		[],
	);

	const scrollToBottom = useCallback((chatId: string) => messageEndRefs.current[chatId]?.scrollIntoView({ behavior: "smooth" }), []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: showErrorSnackbar re-renders too often
	const handleSendMessage = useCallback(
		(chatId: string) => async (e: FormEvent) => {
			e.preventDefault();

			const chat = chats.find((c) => c.id === chatId);
			if (!chat || !chat.inputMessage.trim() || chat.isThinking) return;

			const userMessage: ChatMessage = { id: Date.now(), text: chat.inputMessage.trim(), sender: "user", timestamp: new Date().toISOString(), type: "text" };

			updateChat(chatId, { messages: [...chat.messages, userMessage], inputMessage: "", isThinking: true });

			setTimeout(() => scrollToBottom(chatId), 100);

			// Scripted workload questions answer locally; everything else hits the backend.
			const scriptedIntent = isShiftInsightsDemo ? detectAnnieShiftInsightsIntent(userMessage.text) : null;
			if (scriptedIntent) {
				const shownWeek = await readShownWeek().catch((error: unknown) => {
					showErrorSnackbar(t("staffing.week.load.failed"), {}, error);
					return null;
				});
				if (shownWeek === null) {
					updateChat(chatId, { messages: [...chat.messages, userMessage], isThinking: false });
					return;
				}
				const answer = buildAnnieShiftInsightsAnswer(scriptedIntent, shownWeek, DateTime.now(), userMessage.text);
				const serverMessage: ChatMessage = { id: Date.now() + 1, text: answer, sender: "server", timestamp: new Date().toISOString(), type: "text" };
				// Brief pause so the "Thinking…" indicator reads as Annie composing an answer.
				setTimeout(() => {
					updateChat(chatId, { messages: [...chat.messages, userMessage, serverMessage], isThinking: false, streamingMessageId: serverMessage.id });
					setTimeout(() => scrollToBottom(chatId), 100);
					setTimeout(() => updateChat(chatId, { streamingMessageId: null }), serverMessage.text.length * 20 + 500);
				}, 1200);
				return;
			}

			try {
				const response = await sendMessage({ message: userMessage.text, sessionId: chatId, departmentId: chosenDepartment.department_id });

				const serverMessage: ChatMessage = { id: Date.now() + 1, text: response.message, sender: "server", timestamp: new Date().toISOString(), type: "text" };
				const isRemoveNurseDone = response.action === ChatbotActions.RemoveNurse && response.completed === true;
				// Both legacy and real demos use the same scripted reveal (Ashley/Stefanie decline,
				// Kathy accepts). Real demos additionally have a real DB swap done by the backend.
				if ((isAnyDemo || isRealDemo) && isRemoveNurseDone) {
					showDemoRemoveNurse(chat, userMessage, serverMessage, response);
				} else {
					updateChat(chatId, { messages: [...chat.messages, userMessage, serverMessage], isThinking: false, streamingMessageId: serverMessage.id });
					setTimeout(() => scrollToBottom(chatId), 100);
					setTimeout(() => updateChat(chatId, { streamingMessageId: null }), serverMessage.text.length * 20 + 500);
				}
			} catch (error) {
				showErrorSnackbar("Failed to send message:", {}, error);

				updateChat(chatId, {
					messages: [
						...chat.messages,
						userMessage,
						{
							id: Date.now() + 1,
							text: "Sorry, I couldn't process your question. Please try again.",
							sender: "server",
							timestamp: new Date().toISOString(),
							type: "text",
						},
					],
					isThinking: false,
				});

				setTimeout(() => scrollToBottom(chatId), 100);
			}
		},
		[chats, updateChat, scrollToBottom, sendChatBotMessage, isShiftInsightsDemo, readShownWeek],
	);

	const showDemoRemoveNurse = useCallback(
		(chat: Chat, userMessage: ChatMessage, serverMessage: ChatMessage, response: ChatbotMessage) => {
			const chatId = chat.id;
			const messages = [...chat.messages, userMessage, serverMessage];
			updateChat(chatId, { messages: messages, isThinking: false, streamingMessageId: serverMessage.id });
			setTimeout(() => scrollToBottom(chatId), 300);

			const demoMessage: ChatMessage = {
				id: Date.now() + 1,
				text: response.message,
				sender: "server",
				timestamp: new Date().toISOString(),
				type: "task",
			};
			const demoMessages = [...messages, demoMessage];
			setTimeout(() => updateChat(chatId, { messages: demoMessages, isThinking: false, streamingMessageId: null }), 3000);
			setTimeout(() => scrollToBottom(chatId), 3100);

			// Real demos send the actual nurses the backend picked (in response.parameters);
			// legacy demos have none, so fall back to the scripted Ashley/Stefanie/Kathy.
			const decliners: string[] = response.parameters?.replacementCandidates ?? ["Ashley", "Stefanie"];
			const replacementName: string = response.parameters?.replacementName ?? "Kathy Castillo";

			const [decliner1, decliner2] = decliners;
			const msg1 = getNurseDemoPickupMessage(decliner1, false);
			const messages1 = [...demoMessages, msg1];
			setTimeout(() => updateChat(chatId, { messages: messages1, isThinking: false, streamingMessageId: msg1.id }), 8000);
			const msg2 = getNurseDemoPickupMessage(decliner2, false);
			const messages2 = [...messages1, msg2];
			setTimeout(() => updateChat(chatId, { messages: messages2, isThinking: false, streamingMessageId: msg2.id }), 12000);

			const msg3 = getNurseDemoPickupMessage(replacementName, true);
			const messages3 = [...messages2, msg3];
			setTimeout(() => {
				updateChat(chatId, { messages: messages3, isThinking: false, streamingMessageId: msg3.id });
				const removeDate = response.parameters?.date ? DateTime.fromISO(response.parameters.date) : DateTime.now();
				replaceDemoNurse(Number(response.parameters?.nurseId), removeDate, replacementName)
					.then((isReplaced) => {
						if (isReplaced) showSnackbar(`Success! ${replacementName.split(" ")[0]} can fill the shift!`, "success");
					})
					.catch((error: unknown) => showErrorSnackbar(t("staffing.week.load.failed"), { nurse_id: response.parameters?.nurseId }, error));
			}, 15000);
			setTimeout(() => updateChat(chatId, { streamingMessageId: null }), msg3.text.length * 20 + 15000);

			setTimeout(() => scrollToBottom(chatId), 16000);
		},
		[updateChat, scrollToBottom, replaceDemoNurse, showSnackbar, showErrorSnackbar, t],
	);

	const handleToggleChat = useCallback(() => {
		setIsOpen((prev) => !prev);
		if (!isOpen && activeChatId) {
			updateChat(activeChatId, { unreadCount: 0 });
			setTimeout(() => scrollToBottom(activeChatId), 100);
		}
	}, [isOpen, activeChatId, updateChat, scrollToBottom]);

	const handleNewChat = useCallback(() => {
		const newChat = createNewChat();
		setChats((prev) => [newChat, ...prev]);
		setActiveChatId(newChat.id);
		setTimeout(() => scrollToBottom(newChat.id), 100);
	}, [scrollToBottom, createNewChat]);

	const handleSwitchChat = useCallback(
		(chatId: string): void => {
			setActiveChatId(chatId);
			updateChat(chatId, { unreadCount: 0 });
			setTimeout(() => scrollToBottom(chatId), 100);
		},
		[updateChat, scrollToBottom],
	);

	useEffect(() => {
		if (!activeChatId || !currentChatMessages) return;

		// Only update if the messages actually belong to the active chat
		const firstMessage = currentChatMessages[0];
		if (firstMessage?.sessionId !== activeChatId) return;

		const messages: ChatMessage[] = currentChatMessages.map((msg) => ({
			id: `${msg.sessionId}-${msg.timestamp}`,
			text: msg.message,
			sender: msg.role === "user" ? "user" : "server",
			timestamp: msg.timestamp,
			type: "text",
		}));

		updateChat(activeChatId, { messages });
	}, [currentChatMessages, activeChatId, updateChat]);

	const handleDeleteChat = useCallback(
		async (chatId: string, e?: MouseEvent) => {
			e?.stopPropagation();

			try {
				await deleteConversation(chatId);

				setChats((prev) => {
					const filtered = prev.filter((chat) => chat.id !== chatId);

					if (filtered.length === 0) {
						const newChat = createNewChat();
						setActiveChatId(newChat.id);
						return [newChat];
					}

					if (chatId === activeChatId) {
						setActiveChatId(filtered[0].id);
					}

					return filtered;
				});
			} catch (error) {
				showErrorSnackbar("Failed to delete conversation", {}, error);
			}
		},
		[activeChatId, createNewChat, deleteConversation, showErrorSnackbar],
	);

	const getChatTitle = useCallback((chat: Chat): string => {
		if (!chat.messages.length) return "New Chat";
		const firstUserMessage = chat.messages.find((m) => m.sender === "user");
		if (!firstUserMessage) return "New Chat";

		return firstUserMessage.text;
	}, []);

	const changeHideStatus = useCallback((openStatus: boolean) => setIsHiding(openStatus), []);

	return (
		<UserGuideChatContext.Provider
			value={{
				chats,
				allConversations,
				activeChat,
				activeChatId,
				isOpen,
				isDrawerOpen,
				isHiding,
				changeHideStatus,
				messageEndRefs,
				updateChat,
				handleSendMessage,
				handleToggleChat,
				handleNewChat,
				handleSwitchChat,
				handleDeleteChat,
				setIsDrawerOpen,
				getChatTitle,
				isDeletingConversation,
				isLoadingAllConversations,
			}}
		>
			{children}
		</UserGuideChatContext.Provider>
	);
};
